import { useRef, useState } from 'react';
import { api } from '../../shared/api/client';
import type { MyNursery, NurseryBoard, NurseryTeam } from '../../shared/api/types';
import { Alert, EmptyState, ErrorState, Loading } from '../../shared/ui/components';
import { useAsync, type AsyncState } from '../../shared/ui/useAsync';
import { useMe } from '../session/SessionProvider';
import { GuardianView } from './GuardianView';
import { RosterView } from './RosterView';
import { StaffBoard } from './StaffBoard';
import { useNurserySocket } from './useNurserySocket';

/** Guardería (Motor 9): una misma pantalla para papás, equipo y rol, cada uno con lo suyo. */
export function NurseryPage() {
  const me = useMe();
  const churchId = me.activeChurchId;
  const canUse = me.can('nursery.use');
  const canOperate = me.can('nursery.operate');
  const [tab, setTab] = useState<Tab>(canOperate || !canUse ? 'rooms' : 'mine');
  // Cada aviso del tiempo real sube la versión y las vistas abiertas vuelven a pedir lo suyo.
  const [version, setVersion] = useState(0);
  useNurserySocket(canUse ? churchId : null, () => setVersion((v) => v + 1));
  const team = useAsync(
    (signal) => (churchId && canUse ? api.get<NurseryTeam>(`/nursery/churches/${churchId}/team`, signal) : Promise.resolve(null)),
    [churchId, canUse, version],
  );
  const teamView = useSticky(team);
  const inTeam = !!teamView && (teamView.role !== null || teamView.canManageRoster);

  if (!churchId) return <EmptyState title="Elige primero la iglesia en la que actúas." />;
  const tabs: [Tab, string][] = [
    ...(canUse ? ([['mine', 'Mis hijos']] as [Tab, string][]) : []),
    ...(canOperate ? ([['rooms', 'Salas']] as [Tab, string][]) : []),
    ...(inTeam ? ([['roster', 'Turnos']] as [Tab, string][]) : []),
  ];
  const current = tabs.some(([t]) => t === tab) ? tab : (tabs[0]?.[0] ?? 'mine');
  return (
    <div className="page page--narrow">
      <header className="page__header">
        <h1>Guardería</h1>
        {tabs.length > 1 ? (
          <div className="chips" role="tablist">
            {tabs.map(([t, label]) => (
              <button key={t} type="button" role="tab" aria-selected={current === t} className={`chip${current === t ? ' chip--active' : ''}`} onClick={() => setTab(t)}>
                {label}
              </button>
            ))}
          </div>
        ) : null}
      </header>
      {teamView?.onDutyToday && current !== 'rooms' ? <Alert>Hoy te toca cuidar en la guardería: abre la pestaña Salas.</Alert> : null}
      {current === 'mine' && canUse ? <Mine churchId={churchId} version={version} /> : null}
      {current === 'rooms' && canOperate ? <Rooms churchId={churchId} version={version} /> : null}
      {current === 'roster' && teamView ? <RosterView churchId={churchId} team={teamView} version={version} /> : null}
    </div>
  );
}

type Tab = 'mine' | 'rooms' | 'roster';

/** Conserva lo último mientras recarga (las acciones recargan la vista). */
function useSticky<T>(state: AsyncState<T>) {
  const last = useRef<T | null>(null);
  if (state.status === 'ready') last.current = state.data;
  return state.status === 'ready' ? state.data : last.current;
}

function Mine({ churchId, version }: { churchId: string; version: number }) {
  const data = useAsync((signal) => api.get<MyNursery>(`/nursery/churches/${churchId}/mine`, signal), [churchId, version]);
  const view = useSticky(data);
  if (data.status === 'error') return <ErrorState error={data.error} onRetry={data.reload} />;
  if (!view) return <Loading />;
  return <GuardianView churchId={churchId} data={view} onChange={data.reload} />;
}

function Rooms({ churchId, version }: { churchId: string; version: number }) {
  const data = useAsync((signal) => api.get<NurseryBoard>(`/nursery/churches/${churchId}/board`, signal), [churchId, version]);
  const view = useSticky(data);
  if (data.status === 'error') return <ErrorState error={data.error} onRetry={data.reload} />;
  if (!view) return <Loading />;
  return <StaffBoard churchId={churchId} board={view} onChange={data.reload} />;
}
